import { FC, useState } from "react";
import SubmitItem from "../../../components/template/SubmitItem/SubmitItem";
import { FieldValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Toast } from "../../../components/atom/Toast/Toast";
import { OrganizationItemSchema } from "../data/OrganizationItemSchema";
import { ORGANIZATION_URL } from "../../../utils/endpoints";
import { handleSubmitItem } from "../../../helpers/handleSubmitItem";
import OrganizationSubmitForm from "./OrganizationSubmitForm";
import { useStepper } from "../../../hooks/stepper/useStepper";
import { StepperConfig } from "../../../types/Components/Stepper/StepperConfig";
import { AlertDialogConfig } from "../../../types/Components/Alert/AlertDialogConfig";
import api from "../../../utils/api";

const NewOrganization: FC = () => {
  const { addToast } = Toast();
  const navigate = useNavigate();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
    trigger,
    resetField,
  } = useForm({
    resolver: zodResolver(OrganizationItemSchema),
    mode: "onChange",
    shouldUseNativeValidation: false,
  });

  const onSubmit = async (data: FieldValues) => {
    const bodyRequest = {
      name: data.name,
      email: data.email ? data.email : null,
      province: data.provinceOption ? data.provinceOption.id : null,
      county: data.countyOption ? data.countyOption.id : null,
      organizationLevel: data.organizationLevelOption.id,
    };

    await handleSubmitItem(
      () => api.post(ORGANIZATION_URL, bodyRequest),
      addToast,
      setIsLoaded,
      "Create",
      "Entidade Criada",
    );
    navigate("/entidades");
  };

  const steps = [
    { title: "Preencher as informações da entidade", description: "Passo 1" },
    { title: "Verificar as informações da entidade", description: "Passo 2" },
  ];

  const { currentStep, handleNext, handleBack, handleFinalSubmit } = useStepper(
    steps,
    trigger,
    handleSubmit,
    onSubmit,
  );

  const stepperConfig: StepperConfig = {
    nextButtonName: "Criar Entidade",
    steps,
    onNext: currentStep === steps.length - 1 ? handleFinalSubmit : handleNext,
    onBack: handleBack,
    isNextActive: true,
    isFinalStep: currentStep === steps.length - 1,
    currentStep,
  };

  const alertDialogConfig: AlertDialogConfig = {
    setIsAlertOpen,
    isAlertOpen,
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <SubmitItem
        title="Criar Entidade"
        formTitle="Dados institucionais"
        hasAlert={currentStep === steps.length - 1}
        hasSubmitFooter
        hasStepper
        stepperConfig={stepperConfig}
        alertDialogConfig={alertDialogConfig}
        isLoaded={isLoaded}
        isDirty={isDirty}
      >
        <OrganizationSubmitForm
          errors={errors}
          control={control}
          resetField={resetField}
          watch={watch}
          isDisabled={currentStep === steps.length - 1}
        />
      </SubmitItem>
    </form>
  );
};

export default NewOrganization;
